import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { Logo } from "@/components/brand/Logo";
import { UserProfileTrigger } from "@/components/layout/navigation/UserProfileTrigger";
import { HeaderMobileNav } from "./HeaderMobileNav";
import { useAdminStatus } from "@/hooks/useAdminStatus";

/**
 * Header.
 */
export function Header() {
  const { user } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isAdmin } = useAdminStatus();

  // Check if user is logged in and not on auth-related pages
  const isLoggedIn = !!user;
  const isAuthPage = ["/auth", "/signin", "/signup"].includes(location.pathname);
  const showUserInterface = isLoggedIn && !isAuthPage;

  return (
    <header
      className="w-full py-3 px-4 border-b bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50"
      aria-label="TellerWand site header"
    >
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* Mobile Hamburger Menu - Only show on mobile for logged-in users */}
          {showUserInterface ? (
            <HeaderMobileNav
              isOpen={isSidebarOpen}
              onOpenChange={setIsSidebarOpen}
              isAdmin={isAdmin}
            />
          ) : null}

          {/* Always show Logo */}
          <Link
            to={isLoggedIn ? "/dashboard" : "/"}
            aria-label="TellerWand home"
            className="hover:opacity-80 transition-opacity"
          >
            <Logo showText={true} className="scale-110" linkToHome={false} />
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {showUserInterface ? (
            // User is logged in - show user account options
            <UserProfileTrigger variant="header" />
          ) : (
            // User is not logged in - show navigation + login/signup buttons
            <>
              <nav className="hidden md:flex items-center gap-4">
                <Link
                  to="/features"
                  className="text-sm text-gray-700 hover:text-orange-600 transition-colors"
                >
                  Features
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm text-gray-700 hover:text-orange-600 transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/blog"
                  className="text-sm text-gray-700 hover:text-orange-600 transition-colors"
                >
                  Blog
                </Link>
                <Link
                  to="/faq"
                  className="text-sm text-gray-700 hover:text-orange-600 transition-colors"
                >
                  FAQ
                </Link>
              </nav>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" asChild>
                  <PostLoginAuthLink>Login</PostLoginAuthLink>
                </Button>
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  asChild
                >
                  <PostLoginAuthLink mode="signup">Sign Up</PostLoginAuthLink>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
